import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { CloseOutlinedIcon } from '../../../../assets/mui';
import { SipFrequency } from './SipOptions';
import StopSipPeriodModal from './StopSipPeriodModal';
import { NetworkManager } from '../../../../services';
import toast from 'react-hot-toast';

interface StopSipModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (period?: '7' | '10' | '15' | '1' | '2' | '3' | 'PERMANENT') => void;
  frequency: SipFrequency;
  subscriptionId?: string;
  refetchSubscriptions?: () => void;
  subscriptionStatus?: 'ACTIVE' | 'PAUSED' | null;
  onResumeSuccess?: () => void;
}

const StopSipModal: React.FC<StopSipModalProps> = ({
  open,
  onClose,
  onConfirm,
  frequency,
  subscriptionId,
  refetchSubscriptions,
  subscriptionStatus,
  onResumeSuccess,
}) => {
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [isResumingSubscription, setIsResumingSubscription] = useState(false);

  const handleStopSaving = () => {
    setShowPeriodModal(true);
  };

  const handlePeriodConfirm = (period: '7' | '10' | '15' | '1' | '2' | '3' | 'PERMANENT') => {
    setShowPeriodModal(false);
    onConfirm(period);
    onClose();
  };

  const handlePeriodClose = () => {
    setShowPeriodModal(false);
    onClose();
  };

  const handleModalClose = () => {
    setShowPeriodModal(false);
    onClose();
  };

  const handleContinueSaving = async () => {
    if (subscriptionStatus === 'PAUSED') {
      if (!subscriptionId) {
        toast.error('Subscription ID not found. Please try again.', {
          duration: 2000,
          position: 'top-center',
        });
        return;
      }

      setIsResumingSubscription(true);
      try {
        const path = NetworkManager.PATH.resumeSipSubscription.replace(
          '{{subscriptionId}}',
          subscriptionId,
        );
        await NetworkManager.Call({
          method: 'post',
          path: path,
        });

        toast.success('SIP resumed successfully', {
          duration: 2000,
          position: 'top-center',
        });

        if (refetchSubscriptions) {
          refetchSubscriptions();
        }

        onClose();

        // Navigate back to SIP options screen after successful resume
        if (onResumeSuccess) {
          onResumeSuccess();
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || 'Failed to resume SIP. Please try again.', {
          duration: 2000,
          position: 'top-center',
        });
      } finally {
        setIsResumingSubscription(false);
      }
    } else {
      onClose();
    }
  };
  return (
    <>
      <Dialog
        open={open && !showPeriodModal}
        maxWidth="sm"
        fullWidth
        onClose={handleModalClose}
        PaperProps={{
          sx: {
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle>
          <div className="px-5 flex justify-end mt-2">
            <CloseOutlinedIcon
              className="cursor-pointer bg-slate-200 rounded-full p-1"
              onClick={handleModalClose}
              style={{
                width: '28px',
                height: '28px',
              }}
            />
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="bg-white rounded-lg px-5 pb-5">
            <div className="flex flex-col gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Are you sure you want to stop your client saving?
                </h3>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleStopSaving}
                  className="flex-1 h-11 rounded-xl border border-gray-200 bg-white text-gray-900 font-semibold shadow-sm"
                >
                  Stop Saving
                </button>
                <button
                  type="button"
                  onClick={
                    subscriptionStatus === 'PAUSED' ? handleContinueSaving : handleModalClose
                  }
                  disabled={isResumingSubscription}
                  className="flex-1 h-11 rounded-xl bg-green-700 text-white font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isResumingSubscription ? 'Resuming...' : 'Continue Saving'}
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <StopSipPeriodModal
        open={showPeriodModal}
        onClose={handlePeriodClose}
        onConfirm={handlePeriodConfirm}
        frequency={frequency}
        subscriptionId={subscriptionId}
        refetchSubscriptions={refetchSubscriptions}
      />
    </>
  );
};

export default StopSipModal;
